import React, { useState, useCallback, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import { apiFetchJson } from '../../apiClient';

function ExpenseSettlement({ period, onClose }) {
  const { instance } = useMsal();
  const { t } = useTranslation();
  const [settlement, setSettlement] = useState(null);
  const [contributions, setContributions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showContribForm, setShowContribForm] = useState(false);
  const [contribForm, setContribForm] = useState({ userId: '', amount: '', type: 'monthly', period: null, description: '' });

  const unwrap = (data) => {
    const arr = Array.isArray(data) ? data : (data?.$values || []);
    return arr.filter(item => item && !item.$ref);
  };

  const loadSettlement = useCallback(async () => {
    setLoading(true);
    try {
      setSettlement(await apiFetchJson(instance, `/api/Expenses/settlement?period=${period}`));
    } catch (err) { console.error('Failed to load settlement:', err); }
    setLoading(false);
  }, [instance, period]);

  const loadContributions = useCallback(async () => {
    try {
      setContributions(unwrap(await apiFetchJson(instance, '/api/Expenses/contributions')));
    } catch (err) { console.error('Failed to load contributions:', err); }
  }, [instance]);

  useEffect(() => {
    loadSettlement();
    loadContributions();
  }, [loadSettlement, loadContributions]);

  const handleAddContribution = async () => {
    if (!contribForm.amount) return;
    try {
      await apiFetchJson(instance, '/api/Expenses/contributions', {
        method: 'POST',
        body: JSON.stringify({
          userId: contribForm.userId || null,
          amount: parseFloat(contribForm.amount),
          type: contribForm.type,
          period: contribForm.type === 'deposit' ? period : contribForm.period,
          description: contribForm.description || null
        })
      });
      await loadContributions();
      await loadSettlement();
      setShowContribForm(false);
      setContribForm({ userId: '', amount: '', type: 'monthly', period: null, description: '' });
    } catch (err) { console.error('Failed to add contribution:', err); }
  };

  const handleDeleteContribution = async (id) => {
    if (!window.confirm(t('expenses.deleteContribution'))) return;
    try {
      await apiFetchJson(instance, `/api/Expenses/contributions/${id}`, { method: 'DELETE' });
      await loadContributions();
      await loadSettlement();
    } catch (err) { console.error('Failed to delete:', err); }
  };

  const periodLabel = new Date(period + '-01').toLocaleDateString('nb-NO', { month: 'long', year: 'numeric' });

  if (loading) {
    return <div className="expense-loading"><div className="expense-spinner" /><p>{t('expenses.calculatingSettlement')}</p></div>;
  }

  if (!settlement || settlement.totalTransactions === 0) {
    return (
      <div className="settlement-empty">
        <p>{t('expenses.noApprovedTransactions', { period: periodLabel })}</p>
        <button className="expense-btn secondary" onClick={onClose}>← {t('common.back')}</button>
      </div>
    );
  }

  const persons = unwrap(settlement.persons);

  return (
    <div className="settlement">
      <div className="expense-upload-header">
        <button className="expense-btn secondary" onClick={onClose}>← {t('common.back')}</button>
        <h2>{t('expenses.settlementTitle')} — {periodLabel}</h2>
      </div>

      {/* Result banner */}
      <div className={`settlement-result ${settlement.result.includes('owes') ? 'owes' : 'settled'}`}>
        <span className="settlement-result-icon">{settlement.result.includes('owes') ? '💸' : '✅'}</span>
        <span className="settlement-result-text">{settlement.result || t('expenses.configureContributions')}</span>
      </div>

      {/* Summary cards */}
      <div className="settlement-summary">
        <div className="settlement-card">
          <span className="settlement-card-label">{t('expenses.commonExpenses')}</span>
          <span className="settlement-card-value">{settlement.totalCommon.toLocaleString('nb-NO', { minimumFractionDigits: 0 })} kr</span>
        </div>
        <div className="settlement-card">
          <span className="settlement-card-label">{t('expenses.commonPool')}</span>
          <span className="settlement-card-value">{settlement.totalPool.toLocaleString('nb-NO', { minimumFractionDigits: 0 })} kr</span>
        </div>
        <div className="settlement-card">
          <span className="settlement-card-label">{t('expenses.remainingToSplit')}</span>
          <span className="settlement-card-value">{settlement.remainingCommon.toLocaleString('nb-NO', { minimumFractionDigits: 0 })} kr</span>
        </div>
      </div>

      {/* Per-person breakdown */}
      <div className="settlement-persons">
        {persons.map(p => (
          <div key={p.userId} className="settlement-person-card">
            <h3>{p.name}</h3>
            <table className="settlement-detail-table">
              <tbody>
                <tr>
                  <td>👫 {t('expenses.commonPaidOnCards')}</td>
                  <td className="amount-col">{p.commonPaid.toLocaleString('nb-NO')} kr</td>
                </tr>
                <tr>
                  <td>🏦 {t('expenses.poolContribution')}</td>
                  <td className="amount-col">−{p.totalContribution.toLocaleString('nb-NO')} kr</td>
                </tr>
                {p.extraDeposits > 0 && (
                  <tr className="detail-sub">
                    <td>&nbsp;&nbsp;↳ {t('expenses.inclExtraDeposits')}</td>
                    <td className="amount-col">{p.extraDeposits.toLocaleString('nb-NO')} kr</td>
                  </tr>
                )}
                <tr className="detail-separator">
                  <td>{t('expenses.outOfPocketCommon')}</td>
                  <td className="amount-col">{p.outOfPocketCommon.toLocaleString('nb-NO')} kr</td>
                </tr>
                <tr>
                  <td>👤 {t('expenses.personalExpenses')}</td>
                  <td className="amount-col">{p.personalPaid.toLocaleString('nb-NO')} kr</td>
                </tr>
                <tr>
                  <td>🔄 {t('expenses.paidForPartner')}</td>
                  <td className="amount-col">{p.crossPayments.toLocaleString('nb-NO')} kr</td>
                </tr>
                <tr className="detail-total">
                  <td>{t('expenses.netBalance')}</td>
                  <td className={`amount-col ${p.totalOwed > 0 ? 'positive' : p.totalOwed < 0 ? 'negative' : ''}`}>
                    {p.totalOwed > 0 ? '+' : ''}{p.totalOwed.toLocaleString('nb-NO')} kr
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ))}
      </div>

      {/* Contributions config */}
      <div className="settlement-contributions">
        <div className="settlement-contrib-header">
          <h3>🏦 {t('expenses.commonAccountContributions')}</h3>
          <button className="expense-btn-sm primary" onClick={() => setShowContribForm(!showContribForm)}>
            {showContribForm ? t('common.cancel') : '+ ' + t('common.add')}
          </button>
        </div>

        {showContribForm && (
          <div className="settlement-contrib-form">
            <select value={contribForm.type} onChange={e => setContribForm(prev => ({ ...prev, type: e.target.value }))}>
              <option value="monthly">{t('expenses.monthlyBase')}</option>
              <option value="deposit">{t('expenses.oneOffDeposit')}</option>
            </select>
            {persons.length > 0 && (
              <select value={contribForm.userId} onChange={e => setContribForm(prev => ({ ...prev, userId: e.target.value }))}>
                <option value="">{t('expenses.selectPerson')}</option>
                {persons.map(p => (
                  <option key={p.userId} value={p.userId}>{p.name}</option>
                ))}
              </select>
            )}
            <input type="number" placeholder={t('expenses.amountKr')} value={contribForm.amount}
              onChange={e => setContribForm(prev => ({ ...prev, amount: e.target.value }))} />
            {contribForm.type === 'deposit' && (
              <input type="text" placeholder={t('expenses.descriptionOptional')} value={contribForm.description}
                onChange={e => setContribForm(prev => ({ ...prev, description: e.target.value }))} />
            )}
            <button className="expense-btn-sm primary" onClick={handleAddContribution}
              disabled={!contribForm.amount || !contribForm.userId}>{t('common.save')}</button>
          </div>
        )}

        {contributions.length > 0 ? (
          <div className="settlement-contrib-list">
            {contributions.map(c => (
              <div key={c.id} className="settlement-contrib-item">
                <div>
                  <strong>{c.userName}</strong>
                  <span className={`contrib-badge ${c.type}`}>{c.type === 'monthly' ? `📅 ${t('expenses.monthlyLabel')}` : `💰 ${t('expenses.depositLabel')}`}</span>
                  {c.period && <span className="contrib-period">{c.period}</span>}
                  {c.description && <span className="contrib-desc">{c.description}</span>}
                </div>
                <div className="contrib-amount-actions">
                  <span className="contrib-amount">{c.amount.toLocaleString('nb-NO')} kr</span>
                  <button className="expense-btn-sm secondary" onClick={() => handleDeleteContribution(c.id)}>🗑</button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="settlement-contrib-empty">{t('expenses.noContributions')}</p>
        )}
      </div>
    </div>
  );
}

export default ExpenseSettlement;
